import { useCallback, useState, useEffect, useRef } from "react";
import { useUserContext } from "../../contexts/UserContext";
import { catchError } from "../../services/DaoService";
import { Button, Spin, Empty, Collapse } from "antd";
import { PlusCircleFilled, ReloadOutlined, ThunderboltOutlined, CheckCircleFilled } from "@ant-design/icons";
import { TacheService } from "../../services/TacheService";
import TacheCreateForm from "./TacheCreateForm";
import TacheBacklogItem from "./TacheBacklogItem";
import InlineTacheCreator from './InlineTacheCreator';
import TacheDoneSection from './TacheDoneSection';
import PropTypes from "prop-types";
import styles from "./TacheByAffaireList.module.scss";

export default function TacheByAffaireList({ affaire, isReadOnly = false, canManage = false }) {
  const [paginationData, setPaginationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newModalVisible, setNewModalVisible] = useState(false);
  const { check } = useUserContext();
  const [taches, setTaches] = useState([]);
  const sentinelRef = useRef(null);

  const init = useCallback(() => {
    setLoading(true);
    TacheService.getInstance()
      .search({ affaire_id: affaire.id, resolu: false })
      .then(r => { setPaginationData(r.data); setTaches(r.data.data); })
      .catch(catchError)
      .finally(() => setLoading(false));
  }, [affaire]);

  useEffect(() => { init(); }, [init]);

  const loadMore = useCallback(() => {
    if (!paginationData?.next_page_url || loadingMore) return;
    setLoadingMore(true);
    TacheService.post(paginationData.next_page_url, { affaire_id: affaire.id, resolu: false })
      .then(r => { setPaginationData(r.data); setTaches(prev => [...prev, ...r.data.data]); })
      .catch(catchError)
      .finally(() => setLoadingMore(false));
  }, [paginationData, loadingMore, affaire]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      e => { if (e[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const doneFilters = { affaire_id: affaire.id };

  const handleUpdate = (updated) => {
    setTaches(prev => prev.map(t => (t.uid === updated.uid ? updated : t)));
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>Tâches</span>
        <div className={styles.sectionActions}>
          <Button size="small" icon={<ReloadOutlined />} onClick={init} />
          {canManage && !isReadOnly && (
            <Button size="small" type="primary" icon={<PlusCircleFilled />} onClick={() => setNewModalVisible(true)}>
              Nouvelle tâche
            </Button>
          )}
        </div>
      </div>

      <div className={styles.sectionBody}>
        <Collapse
          defaultActiveKey={['active']}
          ghost={false}
          className={styles.collapse}
          items={[{
            key: 'active',
            label: (
              <span className={styles.collapseLabel}>
                <ThunderboltOutlined style={{ color: '#3E65AC' }} />
                Ouverts
                {paginationData?.total != null && (
                  <span className={styles.collapseCount}>({paginationData.total})</span>
                )}
              </span>
            ),
            children: (
              <Spin spinning={loading && taches.length === 0}>
                {taches.length === 0 && !loading
                  ? (
                    <div style={{ padding: '16px 0' }}>
                      <Empty description="Aucune tâche en cours" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                      {!isReadOnly && canManage && (
                        <InlineTacheCreator affaireId={affaire.id} onCreated={t => setTaches(prev => [...prev, t])} />
                      )}
                    </div>
                  )
                  : (
                    <div>
                      {taches.map(tache => (
                        <div key={tache.uid} style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}>
                          <TacheBacklogItem tache={tache} showAffaire={false} onUpdate={handleUpdate} />
                        </div>
                      ))}
                      <div ref={sentinelRef} style={{ height: 1 }} />
                      {loadingMore && <div style={{ textAlign: 'center', padding: '10px' }}><Spin size="small" /></div>}
                      {!isReadOnly && canManage && (
                        <InlineTacheCreator affaireId={affaire.id} onCreated={t => setTaches(prev => [...prev, t])} />
                      )}
                    </div>
                  )
                }
              </Spin>
            ),
          }]}
        />
        <TacheDoneSection filters={doneFilters} showAffaire={false} />
      </div>

      <TacheCreateForm
        affaire={affaire}
        visible={newModalVisible}
        onCreate={() => { init(); setNewModalVisible(false); }}
        onCancel={() => setNewModalVisible(false)}
      />
    </div>
  );
}

TacheByAffaireList.propTypes = {
  affaire: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  canManage: PropTypes.bool,
};
